import { format } from 'date-fns';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Badge } from '../components/Badge';
import type { Release, RollbackSurface, RolloutEndReason } from '../hooks/useAdminData';
import {
  ROLLBACK_SURFACES,
  useActiveRollout,
  useAuditLogs,
  useEndRollout,
  usePatchRollout,
  usePreviousLiveRelease,
  useRelease,
  useResumeAutoPromotion,
  useRollback,
  useRollbackStatus,
  useStartRollout,
} from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

// ─── Status helpers ──────────────────────────────────────────────────────────

function deployStatusVariant(
  status: string,
): 'success' | 'danger' | 'warning' | 'default' | 'info' {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'danger';
    case 'reverted':
      return 'warning';
    case 'pending':
      return 'info';
    default:
      return 'default';
  }
}

function releaseStatusVariant(
  status: string,
): 'success' | 'danger' | 'warning' | 'default' | 'info' {
  switch (status) {
    case 'live':
      return 'success';
    case 'rolling_out':
      return 'info';
    case 'rolled_back':
      return 'danger';
    case 'superseded':
      return 'warning';
    default:
      return 'default';
  }
}

// ─── Rollback Modal ──────────────────────────────────────────────────────────

interface RollbackModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  releaseId: string;
  isPending: boolean;
  targetRelease: Release | null | undefined;
  isTargetLoading: boolean;
  surface: RollbackSurface;
  onSurfaceChange: (surface: RollbackSurface) => void;
}

const SURFACE_LABELS: Record<RollbackSurface, string> = {
  backend: 'Backend (EC2/Dokploy)',
  admin_panel: 'Admin Panel (Dokploy)',
  mobile_ota: 'Mobile OTA',
};

function RollbackModal({
  open,
  onClose,
  onConfirm,
  isPending,
  targetRelease,
  isTargetLoading,
  surface,
  onSurfaceChange,
}: RollbackModalProps) {
  const titleId = useId();
  const [acknowledged, setAcknowledged] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const checkboxId = useId();

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setAcknowledged(false);
  }

  // Capture previous focus and restore it on close
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      return () => {
        previousFocusRef.current?.focus();
      };
    }
  }, [open]);

  // Move focus to the acknowledgment checkbox when modal opens
  useEffect(() => {
    if (open && checkboxRef.current) {
      checkboxRef.current.focus();
    }
  }, [open]);

  // Escape key closes
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Tab focus trap
  useEffect(() => {
    if (!open) return;
    function handleTab(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const modal = modalRef.current;
      if (!modal) return;
      const focusable = Array.from(
        modal.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        role="presentation"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
      />

      {/* Dialog */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg mx-4 z-10"
      >
        <div className="px-5 py-4 border-b border-border">
          <h2 id={titleId} className="text-lg font-semibold text-text flex items-center gap-2">
            <AlertTriangle size={18} className="text-warning" />
            Confirm Rollback
          </h2>
        </div>

        <div className="p-5 space-y-4">
          {/* R4 — single surface per rollback; pick exactly one. */}
          <fieldset
            className="space-y-2"
            aria-labelledby={`${checkboxId}-surface-label`}
            data-testid="rollback-surface-picker"
          >
            <legend
              id={`${checkboxId}-surface-label`}
              className="text-sm font-medium text-text-secondary"
            >
              Surface to roll back
            </legend>
            {ROLLBACK_SURFACES.map((s) => (
              <label key={s} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rollback-surface"
                  value={s}
                  checked={surface === s}
                  onChange={() => onSurfaceChange(s)}
                  className="accent-primary"
                />
                <span className="text-sm text-text">{SURFACE_LABELS[s]}</span>
              </label>
            ))}
          </fieldset>

          {/* C7 — auto-derived rollback target */}
          {isTargetLoading ? (
            <p className="text-sm text-text-muted">Finding previous live release...</p>
          ) : targetRelease ? (
            <p className="text-sm text-text-secondary">
              Rolling back <strong>{SURFACE_LABELS[surface]}</strong> to release{' '}
              <strong className="font-mono">{targetRelease.commitSha.slice(0, 8)}</strong>
              {targetRelease.createdAt ? (
                <>
                  {' '}
                  from{' '}
                  <time dateTime={targetRelease.createdAt}>
                    {format(new Date(targetRelease.createdAt), 'MMM d, yyyy HH:mm')}
                  </time>
                </>
              ) : null}
              .
            </p>
          ) : (
            <p
              className="text-sm text-danger"
              role="alert"
              data-testid="rollback-no-target-message"
            >
              No prior live release to roll back to.
            </p>
          )}

          <div className="bg-warning/10 border border-warning/30 rounded-lg p-3">
            <p className="text-sm text-warning font-medium">
              Native binary cannot be rolled back from this UI. Use App Store phased release halt
              manually.
            </p>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              ref={checkboxRef}
              id={checkboxId}
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 accent-primary"
              aria-label="I acknowledge native binaries are not rollback-able from here"
            />
            <span className="text-sm text-text-secondary">
              I acknowledge native binaries are not rollback-able from here
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
            <button
              ref={confirmRef}
              type="button"
              onClick={onConfirm}
              disabled={!acknowledged || isPending || !targetRelease || isTargetLoading}
              aria-label="Confirm rollback"
              className="px-4 py-2 text-sm rounded-lg font-medium bg-danger text-white hover:bg-danger/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? 'Processing...' : 'Confirm rollback'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ReleaseDetail page ──────────────────────────────────────────────────────

interface ReleaseDetailProps {
  id: string;
}

export function ReleaseDetail({ id }: ReleaseDetailProps) {
  const { data: release, isLoading } = useRelease(id);
  const { data: activeRollout } = useActiveRollout(id);
  const startRollout = useStartRollout();
  const patchRollout = usePatchRollout();
  const endRollout = useEndRollout();
  const rollback = useRollback();
  const { data: rollbackStatus } = useRollbackStatus(id);
  const { data: previousLive, isLoading: isPreviousLiveLoading } = usePreviousLiveRelease(id);
  const { data: auditData } = useAuditLogs({ targetType: 'release', targetId: id });

  const resumeAutoPromotion = useResumeAutoPromotion(id);

  const [rolloutPct, setRolloutPct] = useState(10);
  const [rolloutChannel, setRolloutChannel] = useState('production');
  const [autoPromote, setAutoPromote] = useState(false);
  const [endOutcome, setEndOutcome] = useState<RolloutEndReason>('completed');
  const [rollbackOpen, setRollbackOpen] = useState(false);
  // R4 — single surface per rollback; default 'backend' is the most common.
  const [rollbackSurface, setRollbackSurface] = useState<RollbackSurface>('backend');

  const tooltipId = useId();
  const autoPromoteCheckboxId = useId();

  if (isLoading || !release) {
    return (
      <div className="flex items-center justify-center p-8 text-text-secondary">Loading...</div>
    );
  }

  const activeInstalls = release.clientReports?.length ?? 0;
  const startDisabled = !!activeRollout || release.status !== 'live' || startRollout.isPending;

  function handleStartRollout() {
    startRollout.mutate({ releaseId: id, percentage: rolloutPct, channel: rolloutChannel, autoPromote });
  }

  function handlePatchRollout() {
    patchRollout.mutate({ releaseId: id, percentage: rolloutPct });
  }

  function handleEndRollout() {
    endRollout.mutate({ releaseId: id, outcome: endOutcome });
  }

  function handleRollbackConfirm() {
    // R4 — single surface per call. The backend RollbackService only consumes
    // `surfaces[0]` in V1; sending more would silently roll back only the
    // first and lie to the operator. The Confirm button is disabled when
    // `previousLive` is null, so this should always have a target here.
    if (!previousLive) {
      return;
    }
    rollback.mutate({
      releaseId: id,
      targetReleaseId: previousLive.id,
      surfaces: [rollbackSurface],
    });
    setRollbackOpen(false);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/releases')}
          className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
          aria-label="Back to releases"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold font-mono">
            {release.commitSha.slice(0, 8)}
            <span className="text-text-muted">{release.commitSha.slice(8)}</span>
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-text-secondary">{release.environment}</span>
            <Badge variant={releaseStatusVariant(release.status)}>{release.status}</Badge>
          </div>
        </div>

        {/* Active installs tile */}
        <div className="ml-auto bg-surface border border-border rounded-xl px-5 py-3 text-center">
          <div className="text-2xl font-bold text-text">{activeInstalls}</div>
          <div className="text-xs text-text-muted mt-0.5">Active installs</div>
        </div>
      </div>

      {/* Deployments */}
      <section aria-labelledby="deployments-heading">
        <h2 id="deployments-heading" className="text-lg font-semibold mb-3">
          Deployments
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {release.deployments.map((dep) => (
            <div key={dep.id} className="bg-surface border border-border rounded-xl p-4 space-y-2">
              <div className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                {dep.target}
              </div>
              <Badge variant={deployStatusVariant(dep.status)}>{dep.status}</Badge>
              <div className="text-xs text-text-muted">
                {dep.createdAt ? format(new Date(dep.createdAt), 'MMM d HH:mm') : '—'}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rollout controls */}
      <section aria-labelledby="rollout-heading">
        <h2 id="rollout-heading" className="text-lg font-semibold mb-3">
          Rollout Controls
        </h2>
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4">
          {/* Active rollout info */}
          {activeRollout && (
            <div className="bg-info/10 border border-info/30 rounded-lg p-3 text-sm">
              <p className="text-info font-medium">
                Active rollout: {activeRollout.percentage}% on{' '}
                <strong>{activeRollout.channel}</strong> channel, started{' '}
                {format(new Date(activeRollout.startedAt), 'MMM d, yyyy HH:mm')}
              </p>
            </div>
          )}

          {/* Auto-promote status */}
          {activeRollout?.autoPromote && (
            <div className="rounded-lg p-3 text-sm border border-border">
              {activeRollout.pausedAt ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-warning font-medium" role="status">
                    Auto-promotion paused: {activeRollout.pauseReason ?? 'unknown'}
                  </p>
                  <div className="flex flex-col items-end gap-1">
                    <button
                      type="button"
                      aria-label="Resume auto-promotion"
                      onClick={() => resumeAutoPromotion.mutate()}
                      disabled={resumeAutoPromotion.isPending}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resumeAutoPromotion.isPending ? 'Resuming...' : 'Resume'}
                    </button>
                    {resumeAutoPromotion.isError && (
                      <p className="text-danger text-xs" role="alert">
                        Resume failed — try again.
                      </p>
                    )}
                  </div>
                </div>
              ) : activeRollout.nextPromoteAt ? (
                <p className="text-text-secondary" role="status">
                  Auto-promoting — next bump:{' '}
                  <time dateTime={activeRollout.nextPromoteAt}>
                    {format(new Date(activeRollout.nextPromoteAt), 'PPp')}
                  </time>
                </p>
              ) : (
                <p className="text-success font-medium" role="status">
                  Auto-promotion complete (100%)
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-4 items-end">
            {/* Channel selector */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-text-secondary font-medium" htmlFor="rollout-channel">
                Channel
              </label>
              <select
                id="rollout-channel"
                value={rolloutChannel}
                onChange={(e) => setRolloutChannel(e.target.value)}
                className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
              >
                <option value="production">production</option>
                <option value="staging">staging</option>
              </select>
            </div>

            {/* Percentage slider */}
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label
                className="text-xs text-text-secondary font-medium"
                htmlFor="rollout-percentage"
              >
                Percentage: {rolloutPct}%
              </label>
              <input
                id="rollout-percentage"
                type="range"
                min={1}
                max={100}
                value={rolloutPct}
                onChange={(e) => setRolloutPct(Number(e.target.value))}
                className="accent-primary w-full"
                aria-valuemin={1}
                aria-valuemax={100}
                aria-valuenow={rolloutPct}
              />
            </div>

            {/* Auto-promote checkbox */}
            <div className="flex items-center gap-2">
              <input
                id={autoPromoteCheckboxId}
                type="checkbox"
                checked={autoPromote}
                onChange={(e) => setAutoPromote(e.target.checked)}
                className="accent-primary"
                disabled={startDisabled}
              />
              <label
                htmlFor={autoPromoteCheckboxId}
                className="text-sm text-text-secondary cursor-pointer select-none"
              >
                Auto-promote
              </label>
            </div>

            {/* Tooltip wrapper for disabled Start button */}
            <div className="relative">
              {startDisabled && activeRollout && (
                <div
                  id={tooltipId}
                  role="tooltip"
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-text-secondary whitespace-nowrap shadow-md z-10 pointer-events-none"
                >
                  Active rollout: {activeRollout.percentage}% since{' '}
                  {format(new Date(activeRollout.startedAt), 'MMM d, yyyy')}
                </div>
              )}
              <button
                type="button"
                onClick={handleStartRollout}
                disabled={startDisabled}
                aria-describedby={startDisabled && activeRollout ? tooltipId : undefined}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {startRollout.isPending ? 'Starting...' : 'Start Rollout'}
              </button>
            </div>

            {/* Patch rollout (only when active) */}
            {activeRollout && (
              <button
                type="button"
                onClick={handlePatchRollout}
                disabled={patchRollout.isPending}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-primary text-primary hover:bg-primary-muted transition-colors disabled:opacity-50"
              >
                {patchRollout.isPending ? 'Updating...' : 'Update %'}
              </button>
            )}
          </div>

          {/* End rollout (only when active) */}
          {activeRollout && (
            <div className="flex items-center gap-3 pt-2 border-t border-border">
              <label className="text-xs text-text-secondary font-medium" htmlFor="end-outcome">
                Outcome
              </label>
              <select
                id="end-outcome"
                value={endOutcome}
                onChange={(e) => setEndOutcome(e.target.value as RolloutEndReason)}
                className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
              >
                <option value="completed">completed</option>
                <option value="rolled_back">rolled_back</option>
                <option value="superseded">superseded</option>
              </select>
              <button
                type="button"
                onClick={handleEndRollout}
                disabled={endRollout.isPending}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-warning text-background hover:bg-warning/80 transition-colors disabled:opacity-50"
              >
                {endRollout.isPending ? 'Ending...' : 'End Rollout'}
              </button>
            </div>
          )}

          {/* Rollback button */}
          <div className="pt-2 border-t border-border">
            <button
              type="button"
              onClick={() => setRollbackOpen(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 transition-colors"
            >
              Rollback
            </button>
            {rollbackStatus && rollbackStatus.conclusion && (
              <span className="ml-3 text-xs text-text-muted">
                Last rollback: {rollbackStatus.conclusion} at{' '}
                {format(new Date(rollbackStatus.polledAt), 'MMM d HH:mm')}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Audit timeline */}
      <section aria-labelledby="timeline-heading">
        <h2 id="timeline-heading" className="text-lg font-semibold mb-3">
          Audit Timeline
        </h2>
        <div className="bg-surface border border-border rounded-xl">
          {auditData?.data && auditData.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {auditData.data.map((entry) => (
                <li key={entry.id} className="px-5 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text">{entry.action}</span>
                      <span className="text-xs text-text-muted">by {entry.adminName}</span>
                    </div>
                    <div className="text-xs text-text-muted mt-0.5">
                      {format(new Date(entry.createdAt), 'MMM d, yyyy HH:mm:ss')}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-5 py-6 text-sm text-text-muted text-center">
              No audit events yet.
            </div>
          )}
        </div>
      </section>

      {/* Rollback Modal */}
      <RollbackModal
        open={rollbackOpen}
        onClose={() => setRollbackOpen(false)}
        onConfirm={handleRollbackConfirm}
        releaseId={id}
        isPending={rollback.isPending}
        targetRelease={previousLive}
        isTargetLoading={isPreviousLiveLoading}
        surface={rollbackSurface}
        onSurfaceChange={setRollbackSurface}
      />
    </div>
  );
}
